/**
 * FP3-075 — Account Preferences card.
 *
 * Lives inside the Account → Workspace page. Two independent
 * toggles backed by `PATCH /api/account/preferences`:
 *
 *   • Interface Mode:        Simple | Advanced
 *   • Service Fidelity:      On     | Off
 *
 * Optimistic update + toast confirmation. Server resolves any unknown
 * value to the calm defaults (Advanced + Fidelity On) so existing
 * users see no change unless they explicitly flip a toggle.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sliders, Gauge } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const SEGMENTS = {
  ui_mode: [
    {
      key: "simple",
      label: "Simple",
      hint: "Calmer day-to-day. Hides Wizard, rules, and health tools behind an Advanced disclosure.",
    },
    {
      key: "advanced",
      label: "Advanced",
      hint: "All tools visible. Best for power users and school/team scheduling.",
    },
  ],
  fidelity_monitoring: [
    {
      key: "on",
      label: "On",
      hint: "Today shows Minutes Fidelity strip. Service Fidelity nav entry visible.",
    },
    {
      key: "off",
      label: "Off",
      hint: "Hides the Minutes Fidelity strip on Today and the Service Fidelity nav entry. Goals, sessions, and progress are unchanged.",
    },
  ],
};

function ToggleGroup({ value, options, onChange, testidPrefix, disabled }) {
  return (
    <div
      data-testid={`${testidPrefix}-group`}
      className="inline-flex rounded-lg border border-[#E5E3DB] bg-[#F9F8F6] p-0.5"
    >
      {options.map((opt) => {
        const active = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            data-testid={`${testidPrefix}-${opt.key}`}
            disabled={disabled || active}
            onClick={() => onChange(opt.key)}
            className={`px-3.5 py-1.5 rounded-md text-[12.5px] font-medium transition-all ${
              active
                ? "bg-white text-[#1E3F33] shadow-sm cursor-default"
                : "text-[#6B7280] hover:text-[#1F2937] hover:bg-white/50"
            } ${disabled ? "opacity-50" : ""}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export default function PreferencesCard() {
  const { preferences, refresh } = useAuth();
  const [saving, setSaving] = useState(null); // "ui_mode" | "fidelity_monitoring" | null

  const handleChange = async (field, nextValue) => {
    if (!preferences || preferences[field] === nextValue) return;
    setSaving(field);
    try {
      await api.updateAccountPreferences({ [field]: nextValue });
      await refresh();
      const label =
        field === "ui_mode"
          ? `Interface set to ${nextValue === "simple" ? "Simple" : "Advanced"} Mode`
          : `Service Fidelity ${nextValue === "on" ? "turned on" : "turned off"}`;
      toast.success(label);
    } catch {
      toast.error("Could not save preferences");
    } finally {
      setSaving(null);
    }
  };

  const uiMode = preferences?.ui_mode || "advanced";
  const fidelity = preferences?.fidelity_monitoring || "on";

  const uiHint =
    SEGMENTS.ui_mode.find((s) => s.key === uiMode)?.hint ||
    SEGMENTS.ui_mode[1].hint;
  const fidHint =
    SEGMENTS.fidelity_monitoring.find((s) => s.key === fidelity)?.hint ||
    SEGMENTS.fidelity_monitoring[0].hint;

  return (
    <Card
      data-testid="workspace-info-preferences"
      className="md:col-span-2 border border-[#E5E3DB] shadow-sm"
    >
      <CardHeader className="pb-3 flex-row items-center gap-2">
        <Sliders size={18} weight="duotone" className="text-[#1E3F33]" />
        <CardTitle className="text-[15px] font-semibold heading-font">
          Preferences
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-5">
        <p className="text-[12px] text-[#6B7280] -mt-1.5">
          Tune Frame Practice to match how you like to work. These settings
          only affect <em>your</em> view — they don&rsquo;t change data,
          schedules, or how anyone else sees the app.
        </p>

        {/* Interface Mode */}
        <div className="grid sm:grid-cols-[200px,1fr] gap-3 items-start">
          <div>
            <p className="text-[12.5px] font-semibold text-[#1F2937]">
              Interface Mode
            </p>
            <p className="text-[11px] text-[#9AA0A6] mt-0.5">
              Calm vs. full toolbelt.
            </p>
          </div>
          <div className="space-y-1.5">
            <ToggleGroup
              value={uiMode}
              options={SEGMENTS.ui_mode}
              testidPrefix="pref-ui-mode"
              disabled={saving === "ui_mode"}
              onChange={(v) => handleChange("ui_mode", v)}
            />
            <p
              data-testid="pref-ui-mode-hint"
              className="text-[11.5px] text-[#6B7280] max-w-md leading-relaxed"
            >
              {uiHint}
            </p>
          </div>
        </div>

        {/* Service Fidelity Monitoring */}
        <div className="grid sm:grid-cols-[200px,1fr] gap-3 items-start pt-4 border-t border-[#F1F2F4]">
          <div>
            <p className="text-[12.5px] font-semibold text-[#1F2937] inline-flex items-center gap-1.5">
              <Gauge size={13} weight="duotone" className="text-[#1E3F33]" />
              Service Fidelity Monitoring
            </p>
            <p className="text-[11px] text-[#9AA0A6] mt-0.5">
              Minutes-against-commitment view.
            </p>
          </div>
          <div className="space-y-1.5">
            <ToggleGroup
              value={fidelity}
              options={SEGMENTS.fidelity_monitoring}
              testidPrefix="pref-fidelity"
              disabled={saving === "fidelity_monitoring"}
              onChange={(v) => handleChange("fidelity_monitoring", v)}
            />
            <p
              data-testid="pref-fidelity-hint"
              className="text-[11.5px] text-[#6B7280] max-w-md leading-relaxed"
            >
              {fidHint}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
